import type { ClientGame, ClientGameCtx } from "./index";
import { sound } from "../sound";
import { Fx } from "../fx";
import { roundRectPath } from "../draw";
import { countdownOverlay } from "./asteroids";
import { WB, adjacent, pathWord } from "../../shared/wordgrid";
import { C, TINT, displayFont, bodyFont, hudPill } from "../theme";

// a sunny mint table, white letter stickers, the chain you're dragging in
// bubblegum — the light sticker palette (theme.ts), matching the native
// app's WordBattleGame.tsx
const SCENE = TINT.mint;
const PATH = C.bubblegum;
const MINE = C.green;
const RIVAL = C.coral;
const FREEZE = C.cobalt;
const STEAL = C.mango;
/** mint board panel padding around the tiles, CSS px */
const BOARD_PAD = 10;
/** max side margin outside the board, CSS px */
const SIDE = 6;
/** the bottom strip that holds the .wb-power button (bottom 20px, ~46px tall) */
const POWER_H = 70;
/** the found-words band under the board: label + two chip rows + the power strip */
const BAND_H = 26 + 2 * 32 + POWER_H;

/** env(safe-area-inset-*) in CSS px — the page uses viewport-fit=cover */
function readSafeInsets(): { top: number; bottom: number } {
  try {
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;left:0;top:0;visibility:hidden;pointer-events:none;" +
      "padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom)";
    document.body.appendChild(probe);
    const cs = getComputedStyle(probe);
    const r = { top: parseFloat(cs.paddingTop) || 0, bottom: parseFloat(cs.paddingBottom) || 0 };
    probe.remove();
    return r;
  } catch {
    return { top: 0, bottom: 0 };
  }
}

/** a white sticker pill: [green disc] You 12 : 8 Rival [coral disc], centred on x */
function versusPill(g: CanvasRenderingContext2D, x: number, y: number, mine: number, rival: number) {
  const h = 38, dot = 7;
  const a = String(mine), b = String(rival);
  g.save();
  g.font = bodyFont(12, 900);
  const youW = g.measureText("You").width;
  const rivW = g.measureText("Rival").width;
  g.font = displayFont(20);
  const aw = g.measureText(a).width, bw = g.measureText(b).width, cw = g.measureText(":").width;
  const w = Math.round(14 + dot * 2 + 6 + youW + 8 + aw + 10 + cw + 10 + bw + 8 + rivW + 6 + dot * 2 + 14);
  const left = x - w / 2;
  g.fillStyle = C.ink;
  roundRectPath(g, left, y + 3, w, h, h / 2);
  g.fill();
  roundRectPath(g, left, y, w, h, h / 2);
  g.fillStyle = C.white;
  g.fill();
  g.strokeStyle = C.ink;
  g.lineWidth = 2.5;
  g.stroke();
  const my = y + h / 2;
  g.textBaseline = "middle";
  g.textAlign = "left";
  let cx = left + 14;
  const disc = (col: string) => {
    g.beginPath(); g.arc(cx + dot, my, dot, 0, Math.PI * 2);
    g.fillStyle = col; g.fill();
    g.lineWidth = 2; g.stroke();
    cx += dot * 2 + 6;
  };
  const text = (t: string, font: string, col: string, tw: number, after: number) => {
    g.font = font; g.fillStyle = col;
    g.fillText(t, cx, my + 1);
    cx += tw + after;
  };
  disc(MINE);
  text("You", bodyFont(12, 900), C.muted, youW, 8);
  text(a, displayFont(20), C.ink, aw, 10);
  text(":", displayFont(20), C.muted, cw, 10);
  text(b, displayFont(20), C.ink, bw, 8);
  text("Rival", bodyFont(12, 900), C.muted, rivW, 6);
  disc(RIVAL);
  g.restore();
}

let dictPromise: Promise<Set<string>> | null = null;
function loadDict(): Promise<Set<string>> {
  if (!dictPromise) {
    dictPromise = fetch("/dict.txt")
      .then((r) => r.text())
      .then((t) => new Set(t.split("\n").map((s) => s.trim().toLowerCase()).filter(Boolean)))
      .catch(() => new Set<string>());
  }
  return dictPromise;
}

export class WordBattleClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private fx = new Fx();
  private raf = 0;
  private last = performance.now();
  private t = 0;

  private mySlot = 0;
  private dict = new Set<string>();
  private path: number[] = [];
  private dragging = false;
  private layout = { ox: 0, oy: 0, cell: 60, gap: 8 };
  private tilePop: number[] = new Array(16).fill(0);
  private badFlash = 0;
  private powerBtn!: HTMLButtonElement;
  private myWords: { word: string; pts: number }[] = [];
  /** the board the word list belongs to — a new board (Play again) clears it */
  private wordsGrid = "";
  private safe = { top: 0, bottom: 0 };

  private get st() { return this.ctx.room.state.wordbattle; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    this.mySlot = ctx.mySlot;
    sound.unlock();
    loadDict().then((d) => (this.dict = d));
    this.safe = readSafeInsets();

    this.canvas = document.createElement("canvas");
    this.canvas.id = "game-canvas";
    ctx.root.appendChild(this.canvas);

    this.powerBtn = document.createElement("button");
    this.powerBtn.className = "wb-power";
    this.powerBtn.textContent = "";
    this.powerBtn.style.display = "none";
    this.powerBtn.onclick = (e) => { e.preventDefault(); ctx.room.send("usepower", {}); };
    ctx.root.appendChild(this.powerBtn);

    const hint = document.createElement("div");
    hint.className = "touch-hint";
    hint.textContent = "drag across letters to make words";
    ctx.root.appendChild(hint);
    setTimeout(() => hint.remove(), 3400);

    this.canvas.addEventListener("pointerdown", this.onDown);
    this.canvas.addEventListener("pointermove", this.onMove);
    this.canvas.addEventListener("mousedown", this.onDown as any);
    this.canvas.addEventListener("mousemove", this.onMove as any);
    this.canvas.addEventListener("touchstart", this.onTouch, { passive: false });
    this.canvas.addEventListener("touchmove", this.onTouch, { passive: false });
    window.addEventListener("pointerup", this.onUp);
    window.addEventListener("mouseup", this.onUp);
    window.addEventListener("touchend", this.onUp);

    ctx.room.onMessage("w-word", (m: any) => {
      const mine = m?.slot === this.mySlot;
      (m?.word?.length >= 6 ? sound.wordBig(m.word.length) : sound.wordFound(m?.word?.length || 4));
      (m?.path || []).forEach((c: number) => { this.tilePop[c] = 1; });
      if (mine) {
        this.myWords.push({ word: String(m.word || "").toUpperCase(), pts: m.pts || 0 });
        this.fx.pop(this.tileCX(m.path[m.path.length - 1]), this.tileCY(m.path[0]) - 20, `+${m.pts}`, MINE, 22);
        if (m.word.length >= 6) this.fx.shake(6, 300);
      }
    });
    ctx.room.onMessage("w-steal", (m: any) => {
      if (m?.slot === this.mySlot) this.fx.pop(this.boardCX(), this.layout.oy - 30, `Stole +${m.pts}`, STEAL, 20);
    });
    ctx.room.onMessage("w-bad", (m: any) => {
      if (m?.slot === this.mySlot) { sound.wordBad(); this.badFlash = 1; }
    });
    ctx.room.onMessage("w-earn", (m: any) => {
      if (m?.slot === this.mySlot) {
        const kind = String(m.kind || "");
        this.fx.pop(this.boardCX(), this.layout.oy - 30, `${kind.charAt(0).toUpperCase()}${kind.slice(1)} ready!`, kind === "steal" ? STEAL : FREEZE, 18);
      }
    });
    ctx.room.onMessage("w-power", (m: any) => {
      const onMe = m?.slot !== this.mySlot;
      if (m?.kind === "freeze") { onMe ? sound.powerFreeze() : sound.powerFreeze(); if (onMe) this.fx.flash("rgba(77,124,255,0.3)", 400); }
      else sound.powerSteal();
    });

    this.last = performance.now();
    this.loop();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
    this.powerBtn.remove();
    this.canvas.removeEventListener("pointerdown", this.onDown);
    this.canvas.removeEventListener("pointermove", this.onMove);
    this.canvas.removeEventListener("touchstart", this.onTouch);
    this.canvas.removeEventListener("touchmove", this.onTouch);
    window.removeEventListener("pointerup", this.onUp);
    window.removeEventListener("mouseup", this.onUp);
    window.removeEventListener("touchend", this.onUp);
  }

  private cellAt(clientX: number, clientY: number): number {
    const r = this.canvas.getBoundingClientRect();
    const x = clientX - r.left, y = clientY - r.top;
    const { ox, oy, cell, gap } = this.layout;
    // the hit target is inset well inside each tile so the gaps AND the tile
    // edges are dead zones — a finger grazing a neighbour won't grab it
    const inset = cell * 0.16;
    for (let i = 0; i < 16; i++) {
      const cx = ox + (i % 4) * (cell + gap) + inset;
      const cy = oy + ((i / 4) | 0) * (cell + gap) + inset;
      if (x >= cx && x <= cx + cell - inset * 2 && y >= cy && y <= cy + cell - inset * 2) return i;
    }
    return -1;
  }
  private boardCX() { return this.layout.ox + (this.layout.cell * 4 + this.layout.gap * 3) / 2; }
  private tileCX(i: number) { return this.layout.ox + (i % 4) * (this.layout.cell + this.layout.gap) + this.layout.cell / 2; }
  private tileCY(i: number) { return this.layout.oy + ((i / 4) | 0) * (this.layout.cell + this.layout.gap) + this.layout.cell / 2; }

  private extend(cell: number) {
    if (cell < 0) return;
    const i = this.path.indexOf(cell);
    if (i >= 0) {
      if (i === this.path.length - 2) this.path.pop(); // backtrack
      return;
    }
    if (this.path.length === 0 || adjacent(this.path[this.path.length - 1]!, cell)) {
      this.path.push(cell);
      sound.key();
    }
  }
  private begin(x: number, y: number) {
    if (this.ctx.room.state.phase !== "playing") return;
    if ((this.st.frozen[this.mySlot] ?? 0) > 0) return;
    this.dragging = true;
    this.path = [];
    this.extend(this.cellAt(x, y));
  }
  private commit() {
    if (!this.dragging) return;
    this.dragging = false;
    if (this.path.length >= WB.MIN_LEN) this.ctx.room.send("submit", { path: this.path });
    this.path = [];
  }
  private onDown = (e: PointerEvent | MouseEvent) => { this.begin(e.clientX, e.clientY); e.preventDefault(); };
  private onMove = (e: PointerEvent | MouseEvent) => { if (this.dragging) this.extend(this.cellAt(e.clientX, e.clientY)); };
  private onTouch = (e: TouchEvent) => {
    const t = e.changedTouches[0]; if (!t) return;
    if (e.type === "touchstart") this.begin(t.clientX, t.clientY);
    else if (this.dragging) this.extend(this.cellAt(t.clientX, t.clientY));
    e.preventDefault();
  };
  private onUp = () => this.commit();

  private loop = () => {
    const now = performance.now();
    const dt = Math.min(50, now - this.last);
    this.last = now;
    this.t += dt;
    try { this.draw(dt); } catch (err) { console.error("wordbattle frame", err); }
    this.raf = requestAnimationFrame(this.loop);
  };

  private draw(dt: number) {
    const c = this.canvas;
    const g = c.getContext("2d")!;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const cw = c.clientWidth, ch = c.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (c.width !== cw * dpr || c.height !== ch * dpr) {
      c.width = cw * dpr; c.height = ch * dpr;
      this.safe = readSafeInsets();
    }
    g.save();
    g.scale(dpr, dpr);
    g.lineJoin = "round";
    g.lineCap = "round";

    this.fx.update(dt);
    this.badFlash = Math.max(0, this.badFlash - dt * 0.004);
    for (let i = 0; i < 16; i++) this.tilePop[i] = Math.max(0, this.tilePop[i]! - dt * 0.005);
    const sh = this.fx.shakeXY();

    // a sunny mint table, edge to edge
    g.fillStyle = SCENE;
    g.fillRect(0, 0, cw, ch);

    const st = this.st;
    g.save();
    g.translate(sh.x, sh.y);

    // ---- HUD: time top-right (clear of the quit button and the solo
    // best-score banner), then score / versus centred under the banner ----
    const sT = this.safe.top;
    const secs = Math.ceil((st.timeLeft ?? 0) / 1000);
    hudPill(g, cw - 12, sT + 8, `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`, {
      align: "right", size: 16, label: "Time", fill: secs <= 10 ? C.coral : C.white,
    });
    const rowY = sT + 60;
    const myScore = st.scores[this.mySlot] ?? 0;
    if (st.solo) {
      hudPill(g, cw / 2, rowY, String(myScore), { align: "center", size: 20, label: "Score" });
    } else {
      versusPill(g, cw / 2, rowY, myScore, st.scores[this.mySlot === 0 ? 1 : 0] ?? 0);
    }

    // ---- grid layout ----
    const top = sT + 150;
    const bandTop = ch - this.safe.bottom - BAND_H; // found-words list + power button below the grid
    const bottom = bandTop - 14;
    const avail = Math.max(80, Math.min(cw - 2 * (SIDE + BOARD_PAD), bottom - top));
    const gap = Math.max(12, Math.min(30, avail * 0.06));   // wide gaps so sliding is precise
    const cell = (avail - gap * 3) / 4;
    const ox = (cw - (cell * 4 + gap * 3)) / 2;
    const oy = top + Math.max(0, (bottom - top - (cell * 4 + gap * 3)) / 2);
    this.layout = { ox, oy, cell, gap };
    const gridW = cell * 4 + gap * 3;

    // current word (while dragging), else the steal-window note — a chip
    // centred between the score row and the board
    const chipY = Math.max(rowY + 46, oy - BOARD_PAD - 44);
    const word = pathWord(st.grid, this.path).toUpperCase();
    if (word) {
      const known = this.dict.size === 0 || this.dict.has(word.toLowerCase());
      const fill = word.length < WB.MIN_LEN ? C.white : known ? MINE : TINT.coral;
      hudPill(g, cw / 2, chipY, word, { align: "center", size: 20, fill });
    } else if ((st.stealT[this.mySlot] ?? 0) > 0) {
      hudPill(g, cw / 2, chipY + 4, "✦ Steal on: you skim their next word", { align: "center", size: 13, fill: C.mango });
    }

    // ---- the board: a mint sticker panel under the tiles ----
    const bx = ox - BOARD_PAD, by = oy - BOARD_PAD, bw = gridW + BOARD_PAD * 2;
    const br = Math.max(14, cell * 0.28);
    g.fillStyle = C.ink;
    roundRectPath(g, bx, by + 5, bw, bw, br);
    g.fill();
    roundRectPath(g, bx, by, bw, bw, br);
    g.fillStyle = C.mint;
    g.fill();
    g.strokeStyle = C.ink;
    g.lineWidth = 3;
    g.stroke();

    // path ribbon — drawn under the tiles so it shows as links in the gaps
    if (this.path.length > 1) {
      g.beginPath();
      this.path.forEach((c2, i) => {
        const x = this.tileCX(c2), y = this.tileCY(c2);
        i === 0 ? g.moveTo(x, y) : g.lineTo(x, y);
      });
      g.strokeStyle = C.ink;
      g.lineWidth = cell * 0.2 + 6;
      g.stroke();
      g.strokeStyle = PATH;
      g.lineWidth = cell * 0.2;
      g.stroke();
    }

    // tiles: white letter stickers, ink outline + hard ink shadow; the chain
    // you're dragging presses down in bubblegum, a just-scored word pops green
    const grid = st.grid || "";
    if (grid && grid !== this.wordsGrid) { this.myWords = []; this.wordsGrid = grid; }
    const r = cell * 0.2;
    for (let i = 0; i < 16; i++) {
      const x = ox + (i % 4) * (cell + gap);
      const y = oy + ((i / 4) | 0) * (cell + gap);
      const inPath = this.path.includes(i);
      const pop = this.tilePop[i]!;
      const s = 1 + pop * 0.12;
      const drop = inPath ? 2 : 5;
      g.save();
      g.translate(x + cell / 2, y + cell / 2 + (inPath ? 3 : 0));
      g.scale(s, s);
      g.fillStyle = C.ink;
      roundRectPath(g, -cell / 2, -cell / 2 + drop, cell, cell, r);
      g.fill();
      roundRectPath(g, -cell / 2, -cell / 2, cell, cell, r);
      g.fillStyle = inPath ? PATH : pop > 0 ? MINE : C.white;
      g.fill();
      g.strokeStyle = C.ink;
      g.lineWidth = 3;
      g.stroke();
      const letter = grid[i] === "Q" ? "Qu" : (grid[i] || "");
      g.fillStyle = C.ink;
      g.font = displayFont(cell * (letter.length > 1 ? 0.4 : 0.5));
      g.textAlign = "center"; g.textBaseline = "middle";
      g.fillText(letter, 0, cell * 0.05);
      g.restore();
    }

    // freeze overlay
    if ((st.frozen[this.mySlot] ?? 0) > 0) {
      roundRectPath(g, bx, by, bw, bw, br);
      g.fillStyle = "rgba(77,124,255,0.32)";
      g.fill();
      hudPill(g, cw / 2, oy + gridW / 2 - 18, "❄ Frozen", { align: "center", size: 20, fill: FREEZE, ink: C.white });
    }

    this.fx.draw(g);
    g.restore();

    // bad flash
    if (this.badFlash > 0) {
      g.fillStyle = `rgba(255,107,91,${0.25 * this.badFlash})`;
      g.fillRect(0, 0, cw, ch);
    }

    this.drawWordList(g, cw, bandTop);

    // power button
    const p = st.power[this.mySlot];
    if (p) {
      this.powerBtn.style.display = "";
      this.powerBtn.textContent = p === "freeze" ? "❄ Freeze" : "✦ Steal";
    } else {
      this.powerBtn.style.display = "none";
    }

    this.fx.drawFlash(g, cw, ch);
    if (this.ctx.room.state.phase === "countdown") countdownOverlay(g, cw, ch, this.ctx.room.state.countdown);
    g.restore();
  }

  /** the running list of words this player has found, pinned above the power button */
  private drawWordList(g: CanvasRenderingContext2D, cw: number, bandTop: number) {
    const rowY0 = bandTop + 26;
    const chipH = 26, lineGap = 6, padX = 10, chipGap = 6;
    const left = 14;
    const rightEdge = cw - 14;
    // rows that fit above the power button's strip at the bottom of the band
    const maxRows = Math.max(1, Math.floor((BAND_H - 26 - POWER_H) / (chipH + lineGap)));

    g.textBaseline = "middle";
    g.textAlign = "left";
    g.font = bodyFont(13, 900);
    g.fillStyle = C.ink;
    g.globalAlpha = 0.75;
    g.fillText(`Your words · ${this.myWords.length}`, left, bandTop + 10);
    g.globalAlpha = 1;

    if (!this.myWords.length) {
      g.fillStyle = C.muted;
      g.font = bodyFont(13, 700);
      g.fillText("Words you find show up here", left, rowY0 + chipH / 2);
      return;
    }

    // wrap oldest→newest into rows, then show only the last `maxRows` so the
    // newest word is always on screen
    const ptsR = 9;
    g.font = displayFont(14);
    const chipW = (w: string) => g.measureText(w).width + padX + 6 + ptsR * 2 + 4;
    const rows: { word: string; pts: number }[][] = [[]];
    let x = left;
    for (const item of this.myWords) {
      const w = chipW(item.word);
      if (x + w > rightEdge && rows[rows.length - 1]!.length) { rows.push([]); x = left; }
      rows[rows.length - 1]!.push(item);
      x += w + chipGap;
    }
    const visible = rows.slice(-maxRows);
    visible.forEach((row, ri) => {
      const ry = rowY0 + ri * (chipH + lineGap);
      const fresh = ri === visible.length - 1;
      let rx = left;
      row.forEach((item, ci) => {
        g.font = displayFont(14);
        const w = chipW(item.word);
        const isLatest = fresh && ci === row.length - 1;
        roundRectPath(g, rx, ry, w, chipH, chipH / 2);
        g.fillStyle = isLatest ? MINE : C.white;
        g.fill();
        g.strokeStyle = C.ink;
        g.lineWidth = 2.5;
        g.stroke();
        g.textAlign = "left";
        g.fillStyle = C.ink;
        g.fillText(item.word, rx + padX, ry + chipH / 2 + 1);
        // points in a mango disc at the chip's right end
        const pcx = rx + w - 4 - ptsR, pcy = ry + chipH / 2;
        g.beginPath(); g.arc(pcx, pcy, ptsR, 0, Math.PI * 2);
        g.fillStyle = C.mango;
        g.fill();
        g.lineWidth = 2;
        g.stroke();
        g.textAlign = "center";
        g.fillStyle = C.ink;
        g.font = bodyFont(11, 900);
        g.fillText(`${item.pts}`, pcx, pcy + 0.5);
        rx += w + chipGap;
      });
    });
  }

  results(ctx: ClientGameCtx) {
    const st = ctx.room.state.wordbattle;
    const d = document.createElement("div");
    d.className = "arc-final";
    const me = ctx.mySlot;
    d.innerHTML = st.solo
      ? `<div class="score-big">${st.scores[me] ?? 0}</div><div class="arc-note">${st.found[me] ?? 0} words</div>`
      : `<div class="score-big">${st.scores[0] ?? 0} : ${st.scores[1] ?? 0}</div><div class="arc-note">word battle</div>`;
    return d;
  }
}
